import { isNotFound } from '@betterment/js-runtime';
import { Box } from '@betterment/samba-react';
import { Navigate } from 'react-router-dom';

import CustomerProvider from './CustomerProvider';
import { useCustomerResponse } from './CustomerResponseProvider';
import InactiveCheckingUser from './InactiveCheckingUser';
import { InferLayout } from './InferLayout';
import SignupPending from './SignupPending';

import { WithLayout } from '../../RetailApp/components/WithLayout';
import ErrorFallback from '../../Shared/components/ErrorFallback';
import FullPageLoader from '../../Shared/components/FullPageLoader';
import { ModalContent, ModalOverlay } from '../modal-helpers';
import { canSignUp, isDenied, isManualReview, isSignedUp } from '../models/customer';

const SignedUpCustomerPortal: React.FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  const { customer, error, loading, refetch } = useCustomerResponse();

  if (loading && !customer) {
    return (
      <InferLayout>
        <FullPageLoader />
      </InferLayout>
    );
  }

  const isErrorNotFound = error && isNotFound(error);

  if (error && !isErrorNotFound) {
    return (
      <WithLayout parts={{ sidebar: true, footer: true }}>
        <Box
          css={{
            height: '100vh',
          }}
        >
          <ErrorFallback message="Unable to load account details." />
        </Box>
      </WithLayout>
    );
  }

  if (!customer || canSignUp(customer)) {
    return (
      <WithLayout parts={{ sidebar: false, footer: false }}>
        <Navigate to="/checking/about" />
      </WithLayout>
    );
  }

  if (isManualReview(customer) || isDenied(customer)) {
    return (
      <WithLayout parts={{ sidebar: true, footer: true }}>
        <ModalOverlay>
          <ModalContent aria-label="checking unavailable">
            <InactiveCheckingUser />
          </ModalContent>
        </ModalOverlay>
      </WithLayout>
    );
  }

  if (!isSignedUp(customer)) {
    return (
      <InferLayout>
        <SignupPending message="Hang tight while we set up your account..." reloadCustomer={refetch} />
      </InferLayout>
    );
  }

  return <CustomerProvider {...{ customer, refetch }}>{children}</CustomerProvider>;
};

const CustomerLoader: React.FC<React.PropsWithChildren<unknown>> = ({ children }) => (
  <SignedUpCustomerPortal>{children}</SignedUpCustomerPortal>
);

export default CustomerLoader;
